import React from 'react';
import { ui } from '../../shared/ui';

/** m.46 Triggeri kupnje/prodaje: tipovi i zajednicki elementi. */

export const OZ = 31.1034768;

export type Side = 'buy' | 'sell';
export type Status = 'active' | 'triggered' | 'executed' | 'cancelled' | 'expired';

export type Spot = { eur_per_g: number; eur_per_troy_oz: number; fetched_at: string | null; source: string | null; fresh: boolean; age_minutes: number | null };

export type Meta = {
    spot: Spot;
    can_see_all: boolean;
    me: { id: number; name: string; email: string };
    clients: string[];
    users: Array<{ id: number; name: string }>;
    fresh_minutes: number;
};

export type Trigger = {
    id: number; user_id: number; user_name: string | null; client_name: string; side: Side; metal: string;
    quantity_g: number; target_eur_per_g: number; target_eur_per_oz: number; target_value_eur: number;
    spot_at_create_eur_per_g: number | null; distance_pct: number | null; near_pct: number | null; near_notified_at: string | null;
    notify_emails: string | null; valid_until: string | null; note: string | null; status: Status;
    triggered_at: string | null; triggered_spot_eur_per_g: number | null; mail_sent_at: string | null;
    executed_at: string | null; executed_price_eur_per_g: number | null; executed_qty_g: number | null;
    closed_at: string | null; close_note: string | null; created_at: string;
};

export type Summary = {
    active: number; triggered_open: number; active_buy_g: number; active_sell_g: number; active_buy_eur: number; active_sell_eur: number;
    triggered_today: number; executed_30d_g: number;
    nearest: { id: number; client_name: string; side: Side; distance_pct: number; target_eur_per_g: number; quantity_g: number } | null;
};

export const BUY = '#1f7a4d';
export const SELL = '#b0413e';
export const GOLD = '#b8923a';

export const STATUS_LABEL: Record<Status, string> = {
    active: 'Aktivan', triggered: 'Okinut', executed: 'Izvršen', cancelled: 'Odbačen', expired: 'Istekao',
};
export const STATUS_COLOR: Record<Status, string> = {
    active: '#2f6fb0', triggered: '#c07a12', executed: '#1f7a4d', cancelled: '#8a90a3', expired: '#6b6f7d',
};

const nf = (d: number) => new Intl.NumberFormat('hr-HR', { minimumFractionDigits: d, maximumFractionDigits: d });
export const eur = (v: number | null | undefined, d = 2) => (v == null ? '-' : nf(d).format(v));
export const grams = (v: number | null | undefined) => (v == null ? '-' : new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 3 }).format(v));
export const pct = (v: number | null | undefined, d = 2) => (v == null ? '-' : nf(d).format(v) + ' %');

/** Kolicina za prikaz: >= 1000 g u kg. */
export const qtyShort = (g: number) => (g >= 1000 ? new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 2 }).format(g / 1000) + ' kg' : grams(g) + ' g');

export function dt(v: string | null | undefined): string {
    if (!v) return '-';
    const d = new Date(v.includes('T') ? v : v.replace(' ', 'T'));
    if (isNaN(d.getTime())) return v;
    return d.toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
export function dOnly(v: string | null | undefined): string {
    if (!v) return '-';
    const [y, m, d] = v.slice(0, 10).split('-');
    return `${d}.${m}.${y}.`;
}

export function isHit(side: Side, target: number, spot: number): boolean {
    return side === 'buy' ? spot <= target : spot >= target;
}
export function distancePct(side: Side, target: number, spot: number): number | null {
    if (spot <= 0) return null;
    return (side === 'buy' ? (spot - target) / spot : (target - spot) / spot) * 100;
}

export function SidePill({ side }: { side: Side }) {
    const c = side === 'buy' ? BUY : SELL;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 750, color: c, background: c + '14', border: `1px solid ${c}33`, whiteSpace: 'nowrap' }}>
            {side === 'buy' ? '▼ Kupnja' : '▲ Prodaja'}
        </span>
    );
}

export function StatusPill({ status }: { status: Status }) {
    const c = STATUS_COLOR[status];
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, color: c, background: c + '14', whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: c, boxShadow: status === 'triggered' ? `0 0 0 3px ${c}33` : undefined }} />
            {STATUS_LABEL[status]}
        </span>
    );
}

/** Traka udaljenosti: puna kad je cilj dosegnut, prazna na 10 % i vise. */
export function DistanceBar({ side, distance }: { side: Side; distance: number | null }) {
    if (distance == null) return <span style={{ color: ui.muted }}>-</span>;
    const c = side === 'buy' ? BUY : SELL;
    const fill = Math.max(0.04, Math.min(1, 1 - distance / 10));
    const hot = distance <= 1;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 130 }}>
            <div style={{ flex: 1, height: 7, borderRadius: 99, background: '#eef0f6', overflow: 'hidden' }}>
                <div style={{ width: `${fill * 100}%`, height: '100%', borderRadius: 99, background: hot ? GOLD : c, opacity: hot ? 1 : 0.75, transition: 'width 0.4s' }} />
            </div>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: hot ? GOLD : ui.ink2, minWidth: 52, textAlign: 'right' }}>{distance <= 0 ? 'dosegnut' : pct(distance, 2)}</span>
        </div>
    );
}

export function Modal({ title, onClose, children, width = 560, footer }: { title: string; onClose: () => void; children: React.ReactNode; width?: number; footer?: React.ReactNode }) {
    React.useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);
    return (
        <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,22,40,0.42)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflowY: 'auto' }}>
            <div onMouseDown={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: width, background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(20,22,50,0.28)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${ui.line}` }}>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', color: ui.ink }}>{title}</div>
                    <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '1.3rem', cursor: 'pointer', color: ui.muted }} aria-label="Zatvori">×</button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
                {footer ? <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, padding: '14px 20px', borderTop: `1px solid ${ui.line}`, background: '#fafbfd' }}>{footer}</div> : null}
            </div>
        </div>
    );
}

export function Field({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: '0.76rem', fontWeight: 700, color: ui.ink2, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</span>
            {children}
            {hint ? <span style={{ fontSize: '0.76rem', color: ui.muted }}>{hint}</span> : null}
        </label>
    );
}

export const inputStyle: React.CSSProperties = {
    height: 40, padding: '0 12px', borderRadius: 10, border: `1px solid ${ui.line}`, fontSize: '0.9rem', color: ui.ink, background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box',
};

export function Chip({ children, onClick, active }: { children: React.ReactNode; onClick: () => void; active?: boolean }) {
    return (
        <button type="button" onClick={onClick} style={{ padding: '4px 10px', borderRadius: 999, border: `1px solid ${active ? GOLD : ui.line}`, background: active ? GOLD + '1a' : '#fff', color: active ? '#6b4f1c' : ui.ink2, fontSize: '0.76rem', fontWeight: 650, cursor: 'pointer' }}>
            {children}
        </button>
    );
}

/** Broj iz hrvatskog unosa: "1.234,56" ili "1234.56". */
export function parseNum(raw: string): number | null {
    const s = raw.trim().replace(/\s/g, '');
    if (!s) return null;
    const norm = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = Number(norm);
    return isFinite(n) ? n : null;
}
